import { useCallback, useEffect, useRef, useState } from "react";
import { prefersServerVoiceInput } from "./composerVoiceEnvironment";
import { LiveComposerTranscriptionSession } from "./liveComposerTranscription";
import {
  getSpeechRecognitionConstructor,
  type BrowserSpeechRecognition,
  type SpeechRecognitionErrorEvent,
  type SpeechRecognitionResultEvent,
} from "./speechRecognitionBrowser";

export interface UseComposerVoiceInputOptions {
  getBaseText: () => string;
  onTranscriptChange: (text: string) => void;
  disabled?: boolean;
}

function joinVoiceText(base: string, spoken: string): string {
  const trimmedSpoken = spoken.trim();
  if (!trimmedSpoken) return base;
  const needsSpace = base.length > 0 && !/\s$/.test(base);
  return `${base}${needsSpace ? " " : ""}${trimmedSpoken}`;
}

export function useComposerVoiceInput({
  getBaseText,
  onTranscriptChange,
  disabled = false,
}: UseComposerVoiceInputOptions) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const liveSessionRef = useRef<LiveComposerTranscriptionSession | null>(null);
  const voiceBaseTextRef = useRef("");
  const liveFinalTextRef = useRef("");

  const updateLiveTranscript = useCallback(
    (partial: string) => {
      const combined = joinVoiceText(
        joinVoiceText(voiceBaseTextRef.current, liveFinalTextRef.current),
        partial,
      );
      onTranscriptChange(combined);
    },
    [onTranscriptChange],
  );

  const stopBrowserVoiceInput = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      try {
        recognition.onend = null;
        recognition.onerror = null;
        recognition.onresult = null;
        recognition.stop();
      } catch {
        try {
          recognition.abort();
        } catch {
          // ignore
        }
      }
    }
    setIsListening(false);
  }, []);

  const stopServerVoiceInput = useCallback(async () => {
    const session = liveSessionRef.current;
    liveSessionRef.current = null;
    liveFinalTextRef.current = "";
    if (session) {
      await session.stop().catch(() => undefined);
    }
    setIsListening(false);
  }, []);

  const stopVoiceInput = useCallback(async () => {
    if (liveSessionRef.current) {
      await stopServerVoiceInput();
      return;
    }
    stopBrowserVoiceInput();
  }, [stopBrowserVoiceInput, stopServerVoiceInput]);

  useEffect(() => {
    return () => {
      void stopVoiceInput();
    };
  }, [stopVoiceInput]);

  const startBrowserVoiceInput = useCallback(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionConstructor();
    if (!SpeechRecognitionCtor) {
      alert("Speech Recognition API not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-GB";
    recognition.maxAlternatives = 1;

    voiceBaseTextRef.current = getBaseText();
    recognitionRef.current = recognition;
    setIsListening(true);

    recognition.onresult = (event: SpeechRecognitionResultEvent) => {
      let finalPart = "";
      let interimPart = "";
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) finalPart += transcript;
        else interimPart += transcript;
      }

      const spoken = `${finalPart}${interimPart}`.trim();
      if (!spoken) return;
      onTranscriptChange(joinVoiceText(voiceBaseTextRef.current, spoken));
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === "aborted" || event.error === "no-speech") return;
      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      ) {
        alert(
          "Microphone access might be blocked. Please check your browser's permissions settings.",
        );
      }
      stopBrowserVoiceInput();
    };

    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      try {
        recognition.start();
      } catch {
        stopBrowserVoiceInput();
      }
    };

    recognition.start();
  }, [getBaseText, onTranscriptChange, stopBrowserVoiceInput]);

  const startServerVoiceInput = useCallback(async () => {
    voiceBaseTextRef.current = getBaseText();
    liveFinalTextRef.current = "";

    const session = new LiveComposerTranscriptionSession({
      onPartial: (text) => {
        updateLiveTranscript(text);
      },
      onFinal: (text) => {
        liveFinalTextRef.current = joinVoiceText(
          liveFinalTextRef.current,
          text,
        );
        updateLiveTranscript("");
      },
      onError: (message) => {
        console.error("Live transcription error:", message);
        void stopServerVoiceInput();
      },
    });

    liveSessionRef.current = session;
    setIsListening(true);

    try {
      await session.start();
    } catch (error) {
      console.error("Could not start live transcription:", error);
      const message =
        error instanceof Error ? error.message : "Could not start voice input.";
      alert(message);
      await stopServerVoiceInput();
    }
  }, [getBaseText, stopServerVoiceInput, updateLiveTranscript]);

  const toggleVoiceInput = useCallback(async () => {
    if (disabled) return;
    if (isListening) {
      await stopVoiceInput();
      return;
    }

    if (prefersServerVoiceInput()) {
      await startServerVoiceInput();
      return;
    }

    startBrowserVoiceInput();
  }, [
    disabled,
    isListening,
    startBrowserVoiceInput,
    startServerVoiceInput,
    stopVoiceInput,
  ]);

  return {
    isListening,
    toggleVoiceInput,
  };
}
